import React, { useEffect, useMemo } from 'react';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Divider from '@amzn/meridian/divider';
import Row from '@amzn/meridian/row';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import {
  fetchRegionGroupsAndTasksByRegionId,
  getRegionTasksById,
  setIsCancelModalOpen,
  setJmrResults,
  setPageProgress,
  setRestrictionListItems,
  setSelectedRestrictionOptions,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { JmrPageProgress } from 'src/utils/jmrUtils';
import { setupPreselectedRestrictionOptions, setupRestrictionReviewItems } from 'src/utils/restrictionUtils';
import {
  setupPreselectedRestrictionListItems,
  setupRestrictionReviewItems as setupRestrictionReviewItemsLegacy,
} from 'src/utils/restrictionUtilsLegacy';

import { NewJMRPageFooter, NewJMRPageMainBody, NewJMRPageWrapper } from '../pageLayout/pageLayout';

import BackgroundReview from './backgroundReview/backgroundReview';
import BackgroundReviewLegacy from './backgroundReview/backgroundReviewLegacy';
import PlacementSelectionsReview from './placementSelectionsReview/placementSelectionsReview';
import PlacementSelectionsReviewLegacy from './placementSelectionsReview/placementSelectionsReviewLegacy';
import RestrictionsReview from './restrictionsReview/restrictionsReview';
import RestrictionsReviewLegacy from './restrictionsReview/restrictionsReviewLegacy';

// **** Components **** //

/**
 * Default component
 */
const ReviewPage = () => {
  const dispatch = useAppDispatch();
  const { featureFlags, isFeatureFlagsLoaded } = useAppSelector((state) => state.user);
  const { allowedSites } = useAppSelector((state) => state.sites);
  const {
    backgroundFormData,
    isFetchingRegionGroupsAndTasksByRegionId,
    restrictionOptionsList,
    isFetchingRegionTasksById,
    consolidatedRegionTasks,
    selectedRestrictionOptions,
    selectedTasks,
    restrictionListItems,
    jmrFlowType,
  } = useAppSelector((state) => state.newJmrPage);
  const { shiftLength } = backgroundFormData;
  const showSpinner = isFetchingRegionGroupsAndTasksByRegionId || isFetchingRegionTasksById;

  // Check criteria to determine whether to use the new experience
  const isUsingJobModificationsExperience = useMemo(
    () =>
      featureFlags.JOB_MODIFICATIONS &&
      allowedSites.some((site) => site.site === backgroundFormData.siteLocation && site.jobModificationsEnabled),
    [featureFlags.JOB_MODIFICATIONS, allowedSites],
  );

  // If we're updating, the back-end data may not have been fetched yet.
  useEffect(() => {
    const regionId = backgroundFormData.site?.regionId;
    if (regionId !== undefined && isFeatureFlagsLoaded) {
      if (featureFlags.TASK_GROUPING && !restrictionOptionsList.length) {
        dispatch(fetchRegionGroupsAndTasksByRegionId(regionId));
      } else if (!featureFlags.TASK_GROUPING && !consolidatedRegionTasks.length) {
        dispatch(getRegionTasksById(regionId));
      }
    }
  }, [
    backgroundFormData.site?.regionId,
    isFeatureFlagsLoaded,
    featureFlags.TASK_GROUPING,
    restrictionOptionsList.length,
    consolidatedRegionTasks.length,
  ]);

  // Side effect: initialize preselected options when data is first available
  useEffect(() => {
    if (isFeatureFlagsLoaded) {
      if (featureFlags.TASK_GROUPING && restrictionOptionsList.length && !selectedRestrictionOptions.length) {
        const items = setupPreselectedRestrictionOptions(selectedTasks, restrictionOptionsList, shiftLength);
        dispatch(setSelectedRestrictionOptions(items));
      } else if (!featureFlags.TASK_GROUPING && consolidatedRegionTasks.length && !restrictionListItems.length) {
        const items = setupPreselectedRestrictionListItems(selectedTasks, consolidatedRegionTasks);
        dispatch(setRestrictionListItems(items));
      }
    }
  }, [
    isFeatureFlagsLoaded,
    featureFlags.TASK_GROUPING,
    restrictionOptionsList,
    consolidatedRegionTasks,
    selectedTasks,
    shiftLength,
    selectedRestrictionOptions.length,
    restrictionListItems.length,
  ]);

  // Pure derivation: map selected options to review items
  const restrictions = useMemo(() => {
    if (isFeatureFlagsLoaded) {
      if (featureFlags.TASK_GROUPING && selectedRestrictionOptions.length) {
        return selectedRestrictionOptions.map((task) => setupRestrictionReviewItems(task, shiftLength));
      } else if (!featureFlags.TASK_GROUPING && restrictionListItems.length) {
        return restrictionListItems.map((task) => setupRestrictionReviewItemsLegacy(task, shiftLength));
      }
    }
    return [];
  }, [isFeatureFlagsLoaded, featureFlags.TASK_GROUPING, selectedRestrictionOptions, restrictionListItems, shiftLength]);

  return (
    <NewJMRPageWrapper>
      {/* Main Body */}
      <NewJMRPageMainBody sm="90%" md="75%" lg="50%" title="Review Job Match">
        <LoadingSpinnerWrapper showSpinner={showSpinner}>
          {isUsingJobModificationsExperience ? (
            <>
              <Box spacingInset="400" />
              <BackgroundReview />
              <Box spacingInset="400" />
              <RestrictionsReview
                restrictionReviewItems={restrictions}
                jmrFlowType={jmrFlowType}
                onConfirm={() => {
                  dispatch(setPageProgress(JmrPageProgress.Restrictions));
                  dispatch(setJmrResults([]));
                }}
              />
              <Box spacingInset="400" />
              <PlacementSelectionsReview />
            </>
          ) : (
            <>
              <Divider spacingBefore="500" spacingAfter="300" />
              <BackgroundReviewLegacy />
              <Divider spacingBefore="500" spacingAfter="100" />
              <RestrictionsReviewLegacy />
              <Divider spacingBefore="500" spacingAfter="100" />
              <PlacementSelectionsReviewLegacy />
            </>
          )}
        </LoadingSpinnerWrapper>
      </NewJMRPageMainBody>

      {/* Footer */}
      <NewJMRPageFooter>
        <Row spacing="400">
          <div></div>
          <Button type="link" onClick={() => dispatch(setIsCancelModalOpen(true))}>
            Cancel
          </Button>
          <Button type="tertiary" onClick={() => dispatch(setPageProgress(JmrPageProgress.JobMatches))}>
            Previous
          </Button>
          <Button
            data-cy="new-jmr__review__next"
            onClick={() => dispatch(setPageProgress(JmrPageProgress.SendForApproval))}
          >
            Next
          </Button>
        </Row>
      </NewJMRPageFooter>
    </NewJMRPageWrapper>
  );
};

// **** Export **** //

export default ReviewPage;
